import * as Select from '@radix-ui/react-select';
import { ChevronDown } from '@untitled-ui/icons-react';
import { type ReactNode, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { isMobile } from '../shared/mobile';
import { ErrorField, type ErrorFieldProps } from './inputs/error-field';
import { MenuOption } from './menu-option';

interface SelectFieldProps {
  value: string;
  onChange: (value: string) => void;
  options: Array<{
    value: string;
    label: string | ReactNode;
    separator?: boolean;
    className?: string;
    textClassName?: string;
    rightElement?: ReactNode;
  }>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  triggerClassName?: string;
  triggerIcon?: ReactNode;
  contentClassName?: string;
  itemClassName?: string;
  contentFit?: boolean;
  hideErrorText?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  error?: ErrorFieldProps['error'];
  label?: string;
  description?: string;
  variant?: 'gray' | 'accent';
  tooltip?: ReactNode;
  isNew?: boolean;
  fixedOptionsWidth?: boolean;
}

const sizeClasses = {
  xs: 'py-1.5 px-2.5 h-8',
  sm: 'py-2 px-3.5 h-9',
  md: 'py-2.5 px-4 h-10',
  lg: 'py-2.5 px-4.5 h-11',
};

const selectFontSizeClassNames = {
  xs: 'font-body-2-m',
  sm: 'font-body-2-m',
  md: 'font-body-2-m',
  lg: 'font-body-1-m',
};

export function SelectField(props: SelectFieldProps) {
  const {
    value,
    onChange,
    options,
    className = '',
    disabled = false,
    triggerClassName,
    triggerIcon = <ChevronDown width={16} height={16} />,
    contentClassName,
    contentFit = false,
    placeholder,
    error,
    size = 'md',
    label,
    description,
    variant = 'gray',
    tooltip,
    isNew = false,
    fixedOptionsWidth = false,
  } = props;
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const isMobileDevice = isMobile();

  const triggerClass = twMerge([
    'inline-flex shadow-1 items-center text-black justify-between gap-4 rounded-md bg-white data-[state=open]:border-branding-accent data-[state=open]:ring-2 data-[state=open]:ring-branding-accent-200 font-body-2-m border border-branding-gray outline-none hover:bg-branding-light-gray hover:text-branding-gray',
    disabled ? 'bg-branding-light-gray hover:cursor-not-allowed text-branding-gray' : '',
    sizeClasses[size],
    value != '' ? 'text-black' : 'text-dark-gray',
    selectFontSizeClassNames[size],
    triggerClassName,
    error != null && error != '' ? 'border-red-500' : 'border-branding-gray',
    isNew ? 'px-2' : '',
  ]);

  const trigger = (
    <Select.Trigger className={triggerClass} ref={triggerRef}>
      {fixedOptionsWidth ? (
        <span
          className={twMerge(
            'grid min-w-0 justify-items-start',
            isNew ? 'bg-branding-accent-50 px-2' : ''
          )}
        >
          {options.map(function (option) {
            return (
              <span key={option.value} className="invisible col-start-1 row-start-1" aria-hidden>
                {option.label}
              </span>
            );
          })}
          <span className="col-start-1 row-start-1 truncate">
            {options.find(function (option) {
              return option.value === value;
            })?.label ?? placeholder}
          </span>
        </span>
      ) : (
        <span className={twMerge('truncate', isNew ? 'bg-branding-accent-50 px-2' : '')}>
          {options.find(function (option) {
            return option.value === value;
          })?.label ?? placeholder}
        </span>
      )}
      <Select.Icon>{triggerIcon}</Select.Icon>
    </Select.Trigger>
  );

  const maybeTriggerWithLabel =
    label == null ? (
      trigger
    ) : (
      <label className="grid grid-flow-row grid-cols-1 gap-1.5">
        <div className="flex items-center gap-2">
          <p
            className={twMerge(
              'text-black',
              size === 'md' || size === 'lg' ? 'font-body-2-r' : 'font-body-3-r'
            )}
          >
            {label}
          </p>
          {tooltip}
        </div>
        {trigger}
      </label>
    );

  if (isMobileDevice) {
    const nativeSelectClass = twMerge([
      'inline-flex shadow-1 items-center text-black justify-between gap-4 rounded-md bg-white border border-branding-gray outline-none w-full appearance-none',
      disabled ? 'bg-branding-light-gray cursor-not-allowed text-branding-gray' : '',
      sizeClasses[size],
      value != '' ? 'text-black' : 'text-dark-gray',
      selectFontSizeClassNames[size],
      triggerClassName,
      error != null && error != '' ? 'border-red-500' : 'border-branding-gray',
      isNew ? 'px-2' : '',
    ]);

    const nativeSelect = (
      <div className="relative">
        <select
          value={value}
          onChange={function (e) {
            const newValue = e.target.value;
            if (newValue != '') {
              onChange(newValue);
            }
          }}
          disabled={disabled}
          className={nativeSelectClass}
        >
          {placeholder != null ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {options.map(function (item, index) {
            if (item.separator) {
              return null;
            }
            return (
              <option key={`${item.value}-${index}`} value={item.value}>
                {typeof item.label === 'string' ? item.label : item.value}
              </option>
            );
          })}
        </select>
        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
          {triggerIcon}
        </div>
      </div>
    );

    const maybeNativeSelectWithLabel =
      label == null ? (
        nativeSelect
      ) : (
        <label className="grid grid-flow-row grid-cols-1 gap-1.5">
          <div className="flex items-center gap-2">
            <p
              className={twMerge(
                'text-black',
                size === 'md' || size === 'lg' ? 'font-body-2-r' : 'font-body-3-r'
              )}
            >
              {label}
            </p>
            {tooltip}
          </div>
          {nativeSelect}
        </label>
      );

    return (
      <div className={className}>
        {maybeNativeSelectWithLabel}
        {description == null ? null : (
          <p className="font-body-3-r mt-1.5 text-branding-gray">{description}</p>
        )}
        {!props.hideErrorText ? (
          <div className="mt-[3px]">
            <ErrorField sizeField={props.size} error={error} />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={className}>
      <Select.Root
        value={value}
        onValueChange={function (newValue: string) {
          if (newValue != '') {
            onChange(newValue);
          }
        }}
        disabled={disabled}
      >
        {maybeTriggerWithLabel}
        {description == null ? null : (
          <p className="font-body-3-r mt-1.5 text-branding-gray">{description}</p>
        )}
        <Select.Portal>
          <Select.Content
            position="popper"
            className={twMerge(
              'z-50 mt-2 max-h-60 w-full min-w-[132px] overflow-y-auto rounded-lg border border-branding-gray bg-white p-2',
              contentClassName,
              contentFit ? 'w-[var(--radix-select-trigger-width)]' : ''
            )}
          >
            <Select.Viewport className="SelectViewport">
              {options.map(function (item, index) {
                if (item.separator) {
                  return <Select.Separator key={index} className="m-3 h-px bg-branding-gray" />;
                }
                return (
                  <Select.Item
                    key={`${item.value}-${index}`}
                    value={item.value}
                    className="outline-none"
                  >
                    <Select.ItemText>
                      <MenuOption
                        mainText={item.label as string}
                        className={twMerge(
                          'w-[calc(var(--radix-select-trigger-width)-1rem)]',
                          item.className ?? '',
                          'hover:bg-gray-100'
                        )}
                        textClassName={item.textClassName}
                        variant={variant}
                        rightElement={item.rightElement}
                      />
                    </Select.ItemText>
                  </Select.Item>
                );
              })}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
      {!props.hideErrorText ? (
        <div className="mt-[3px]">
          <ErrorField sizeField={props.size} error={error} />
        </div>
      ) : null}
    </div>
  );
}
